import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { MessageCircle } from 'lucide-react';
import { trackCtaClick, trackWhatsAppClick } from '../utils/analytics';

const LOGO_URL = "/logo-new.png";
const WA_URL = "https://wa.me/918527043856?text=Hi%20CinCin,%20I'm%20planning%20a%20private%20celebration%20and%20would%20love%20to%20connect.";

const experiences = [
  { title: 'Milestone Birthdays', desc: 'Because some years deserve more than a dinner reservation.', image: '/portfolio/drive_private_4.png' },
  { title: 'Anniversaries', desc: 'Designed to honour what you\'ve built together.', image: '/portfolio/drive_private_5.jpg' },
  { title: 'Proposals', desc: 'A moment this important deserves to be designed, not left to chance.', image: '/portfolio/drive_private_6.png' },
  { title: 'Intimate Dinners', desc: 'Where the table setting tells your story before the first course arrives.', image: '/portfolio/drive_process_execute.jpg' },
  { title: 'Private Celebrations', desc: 'Your vision, your space, your moment. We just make it effortless.', image: '/portfolio/drive_private_replace.jpg' },
];

const carousel = [
  '/portfolio/drive_private_6.png', '/portfolio/drive_private_5.jpg', '/portfolio/drive_private_replace.jpg',
  '/portfolio/drive_private_2.png', '/portfolio/drive_private_3.jpg', '/portfolio/drive_private_new.jpg',
  '/portfolio/drive_private_4.png', '/portfolio/drive_private_new_babyshower.jpg',
];

const trust = [
  'Every detail, intentional.',
  'Personal, never templated.',
  'Your celebration, your way.',
  'Designed to stay with you.',
];

function FadeIn({ children, className = '', delay = 0 }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 20 }} animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, delay }} className={className}>
      {children}
    </motion.div>
  );
}

export default function PrivateEventsPage() {
  const [navScrolled, setNavScrolled] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Private Celebrations | CinCin Events';
    const onScroll = () => setNavScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollToContact = () => {
    trackCtaClick('private_hero');
    document.querySelector('#private-contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-[#EFEAE6]">

      {/* Nav */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${navScrolled ? 'nav-glass py-2' : 'bg-transparent py-3'}`}>
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 flex items-center justify-between">
          <Link to="/">
            <img src={LOGO_URL} alt="CinCin Events" className={`w-auto transition-all duration-500 ${navScrolled ? 'h-12 md:h-14' : 'h-16 md:h-20 brightness-0 invert'}`} />
          </Link>
          <button onClick={scrollToContact}
            className={`text-[10px] tracking-[0.18em] uppercase font-light px-6 py-2 rounded-full border transition-all duration-300 ${
              navScrolled ? 'border-[#0A0A0A]/20 text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-[#EFEAE6]' : 'border-white/30 text-white hover:bg-white hover:text-[#0A0A0A]'
            }`}>
            Start Planning
          </button>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative h-screen w-full overflow-hidden">
        <div className="absolute inset-0">
          <img src="/portfolio/drive_private_6.png" alt="Intimate celebration by CinCin" className="w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.15) 50%, transparent 100%)' }} />
        </div>
        <div className="relative z-10 h-full flex flex-col justify-end pb-16 md:pb-24 px-6 md:px-16 lg:px-24">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1.2, delay: 0.3 }} className="max-w-2xl">
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.1] tracking-tight" style={{ textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
              Intimate By Intention.<br /><em className="font-light">Unforgettable By Design.</em>
            </h1>
            <p className="mt-5 text-white/70 text-sm font-light tracking-[0.1em]" style={{ textShadow: '0 1px 3px rgba(0,0,0,0.3)' }}>
              For milestone celebrations that deserve more than ordinary.
            </p>
            <button onClick={scrollToContact} data-testid="private-hero-cta"
              className="mt-8 px-8 py-3.5 rounded-full border border-white/50 text-white text-[11px] tracking-[0.2em] uppercase font-light hover:bg-white hover:text-[#0A0A0A] transition-all duration-500">
              Start Your Celebration
            </button>
          </motion.div>
        </div>
      </section>

      {/* Positioning */}
      <FadeIn className="py-20 md:py-32 px-6 md:px-16 lg:px-24 text-center">
        <div className="max-w-[650px] mx-auto">
          <span className="label-text">Our Approach</span>
          <div className="editorial-line mx-auto mt-4 mb-10" />
          <p className="font-serif text-xl sm:text-2xl leading-[1.7] text-[#0A0A0A]/70">
            We believe the most meaningful celebrations are not the loudest. They are the ones designed around you, your story, and the people who matter most.
          </p>
        </div>
      </FadeIn>

      {/* Experience Blocks */}
      <section className="px-6 md:px-16 lg:px-24 pb-16 md:pb-28">
        <div className="max-w-[1100px] mx-auto space-y-20 md:space-y-28">
          {experiences.map((exp, i) => (
            <FadeIn key={exp.title} delay={0.05}>
              <div className={`flex flex-col ${i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'} gap-8 md:gap-16 items-center`}>
                <div className="w-full md:w-3/5 overflow-hidden">
                  <div className="relative w-full aspect-[4/3]">
                    <img src={exp.image} alt={`${exp.title} by CinCin Events`} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
                  </div>
                </div>
                <div className="w-full md:w-2/5">
                  <h3 className="font-serif text-2xl sm:text-3xl tracking-tight text-[#0A0A0A] leading-[1.15]">{exp.title}</h3>
                  <div className="editorial-line mt-4 mb-5" />
                  <p className="font-light text-[13px] leading-[1.9] text-[#0A0A0A]/55" style={{ letterSpacing: '0.2px' }}>{exp.desc}</p>
                </div>
              </div>
            </FadeIn>
          ))}
        </div>
      </section>

      {/* Carousel */}
      <section className="py-12 md:py-20 overflow-hidden">
        <div className="px-6 md:px-16 mb-8">
          <span className="label-text">Moments We've Designed</span>
        </div>
        <div className="flex gap-[3px] overflow-x-auto scrollbar-hide px-6 md:px-16 pb-4" style={{ scrollSnapType: 'x mandatory' }}>
          {carousel.map((src, i) => (
            <div key={i} className="flex-none w-[75vw] md:w-[35vw] lg:w-[28vw]" style={{ scrollSnapAlign: 'start' }}>
              <div className="relative aspect-[4/3] overflow-hidden">
                <img src={src} alt={`CinCin private celebration ${i + 1}`} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Trust */}
      <FadeIn className="py-16 md:py-24 px-6 md:px-16 lg:px-24">
        <div className="max-w-[800px] mx-auto">
          <span className="label-text">Why CinCin</span>
          <div className="editorial-line mt-4 mb-10" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {trust.map(t => (
              <p key={t} className="font-serif text-base italic text-[#0A0A0A]/50">{t}</p>
            ))}
          </div>
        </div>
      </FadeIn>

      {/* Instagram */}
      <FadeIn className="py-12 md:py-16 px-6 md:px-16 lg:px-24 text-center">
        <p className="text-[10px] tracking-[0.2em] uppercase text-[#8C8C8C]/50 font-light mb-3">Follow Our Work</p>
        <p className="font-light text-[13px] text-[#0A0A0A]/45 mb-6">Explore recent celebrations and behind-the-scenes moments.</p>
        <a href="https://www.instagram.com/cincin.in/" target="_blank" rel="noopener noreferrer"
          className="inline-block px-7 py-2.5 rounded-full border border-[#0A0A0A]/15 text-[#0A0A0A]/50 text-[10px] tracking-[0.18em] uppercase font-light hover:bg-[#0A0A0A] hover:text-[#EFEAE6] transition-all duration-400">
          View on Instagram
        </a>
      </FadeIn>

      {/* CTA */}
      <section id="private-contact" className="relative py-16 md:py-24 px-6 md:px-16 lg:px-24 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src="/portfolio/drive_private_5.jpg" alt="" className="w-full h-full object-cover" style={{ filter: 'blur(16px)', transform: 'scale(1.1)' }} loading="lazy" />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.55), rgba(0,0,0,0.7))' }} />
        </div>
        <div className="relative z-10 max-w-[500px] mx-auto text-center">
          <div className="rounded-xl px-6 py-10 md:px-10 md:py-12" style={{ background: 'rgba(0,0,0,0.35)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}>
            <h2 className="font-serif text-2xl sm:text-3xl text-white tracking-tight leading-[1.2]">Let's create your celebration.</h2>
            <p className="mt-3 text-[12px] text-[#EAEAEA]/40 font-light tracking-wide">We'll personally connect to understand what this moment means to you.</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/#contact" onClick={() => trackCtaClick('private_cta')}
                className="px-8 py-3 rounded-full border border-white/30 text-white text-[11px] tracking-[0.2em] uppercase font-light hover:bg-white hover:text-[#0A0A0A] transition-all duration-500 text-center">
                Start Your Celebration
              </Link>
              <a href={WA_URL} target="_blank" rel="noopener noreferrer" onClick={() => trackWhatsAppClick('private_cta')}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full border border-white/12 text-white/40 text-[10px] tracking-[0.16em] uppercase font-light hover:border-white/25 hover:text-white/60 transition-all">
                <MessageCircle size={12} strokeWidth={1.3} /> WhatsApp
              </a>
            </div>
            <p className="mt-4 text-[10px] text-white/15 font-light tracking-wide">We typically respond within a few hours.</p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#EFEAE6]">
        <div className="border-t border-[#0A0A0A]/6 mx-6 md:mx-16 lg:mx-24" />
        <div className="py-6 md:py-8 px-6 md:px-16 lg:px-24">
          <div className="max-w-[1200px] mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Link to="/"><img src={LOGO_URL} alt="CinCin Events" className="h-10 w-auto" /></Link>
              <span className="font-light text-[10px] text-[#8C8C8C]/50">Designed with intention.</span>
            </div>
            <div className="flex items-center gap-5">
              <a href="https://www.instagram.com/cincin.in/" target="_blank" rel="noopener noreferrer" className="text-[9px] tracking-[0.15em] uppercase text-[#8C8C8C]/50 hover:text-[#0A0A0A] transition-colors">Instagram</a>
              <a href="mailto:hello@cincinevents.com" className="text-[9px] tracking-[0.15em] uppercase text-[#8C8C8C]/50 hover:text-[#0A0A0A] transition-colors">Email</a>
            </div>
          </div>
          <p className="text-center mt-4 text-[8px] tracking-[0.1em] text-[#8C8C8C]/30 font-light">&copy; 2026 CinCin Events. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
